import { z } from "zod";

import { teamDailyDateSchema, teamDailyKennzahlTicketSchema, vorgangstypSchema } from "./teamdaily.ts";

/**
 * Ticket-Blöcke der Bereichs-KPIs (Lieferung 2.1b, Ablösung eazyBI). Alles
 * rechnet aus dem Ticket-Spiegel; gezählt werden **Vorgänge, nicht Ereignisse**
 * (ein zweimal gelöster Vorgang zählt im Zeitraum einmal), damit jede Zahl 1:1
 * in JQL nachstellbar ist und Zahl und Ticketliste dieselbe Länge haben.
 */

/** Die wählbaren Zeiträume für Blöcke ohne Monatsachse (Spec, Bedienung). */
export const TICKET_ZEITRAEUME = [
  "letzte_woche",
  "letzte_30_tage",
  "aktueller_monat",
  "vormonat",
  "letzte_12_monate",
  "aktuelles_jahr",
  "vorjahr",
] as const;
export const ticketZeitraumSchema = z.enum(TICKET_ZEITRAEUME);
export type TicketZeitraum = z.infer<typeof ticketZeitraumSchema>;

/** Ab so viel Verzug des letzten Spiegel-Laufs ist der Stand orange (doppelter 15-Minuten-Takt). */
export const SPIEGEL_VERALTET_MINUTEN = 30;
/** Unter so vielen Vorgängen ist eine SLA-Quote ausgegraut (Spec SR-06). */
export const SLA_MINDESTMENGE = 5;
/** Höchstens so viele Zeilen je Ticketliste; darüber `gekappt`. */
export const TICKET_LISTE_GRENZE = 500;

/** Welche Tickets hinter einem Klick stehen. */
export const KPI_TICKET_QUELLEN = [
  "erstellt",
  "geloest",
  "offen",
  "wiedereroeffnet",
  "sla_verletzt",
  "sla_eingehalten",
] as const;
export const kpiTicketQuelleSchema = z.enum(KPI_TICKET_QUELLEN);
export type KpiTicketQuelle = z.infer<typeof kpiTicketQuelleSchema>;

/** Eine Zeile der Ticketliste — dieselbe Form wie im TeamDaily. */
export const ticketZeileSchema = teamDailyKennzahlTicketSchema;
export type TicketZeile = z.infer<typeof ticketZeileSchema>;

export const spiegelStandSchema = z.object({
  /** Ende des letzten erfolgreichen Spiegel-Laufs (ISO, UTC), sonst null. */
  stand: z.string().nullable(),
  /** Älter als `SPIEGEL_VERALTET_MINUTEN` oder unbekannt. */
  veraltet: z.boolean(),
});
export type SpiegelStand = z.infer<typeof spiegelStandSchema>;

/** Berliner Kalendertage `YYYY-MM-DD`; `bis` ist ausgeschlossen. */
export const zeitraumGrenzenSchema = z.object({ von: z.string(), bis: z.string() });
export type ZeitraumGrenzen = z.infer<typeof zeitraumGrenzenSchema>;

const jahrSchema = z.coerce.number().int().min(2020).max(2100);

/**
 * Eingrenzung aller Ticket-Blöcke auf einen Lead bzw. eine Beauftragung
 * (Lead-Detail, 2.1c). Engt die Kundenabteilungen des Bereichs nur EIN — eine
 * fremde Beauftragung ergibt eine leere Menge, nie fremde Tickets.
 */
export const kpiTicketEingrenzungSchema = z.object({
  leadId: z.string().uuid().optional(),
  beauftragungId: z.string().uuid().optional(),
});
export type KpiTicketEingrenzung = z.infer<typeof kpiTicketEingrenzungSchema>;

/** Abfrage der Blöcke SR-02, SR-05b, SR-06, SR-08. Ohne `zeitraum` gilt das Jahr. */
export const kpiTicketAbfrageSchema = z.object({
  year: jahrSchema,
  zeitraum: ticketZeitraumSchema.optional(),
  vorgangstyp: vorgangstypSchema.optional(),
}).merge(kpiTicketEingrenzungSchema);

/** Abfrage der Leads-Spalten; Vorgabe-Zeitraum letzte 12 Monate (wie eazyBI). */
export const kpiTicketLeadsAbfrageSchema = z.object({
  zeitraum: ticketZeitraumSchema.optional(),
  vorgangstyp: vorgangstypSchema.optional(),
}).merge(kpiTicketEingrenzungSchema);

/**
 * Die Ticketliste hinter einem Klick. `von`/`bis` (Ende ausgeschlossen) liefert
 * der Block aus seiner eigenen Antwort mit; bei `offen` ist `bis` der Stichtag.
 * `von < bis` prüft die Route.
 */
export const kpiTicketListeAbfrageSchema = z.object({
  quelle: kpiTicketQuelleSchema,
  von: teamDailyDateSchema,
  bis: teamDailyDateSchema,
  vorgangstyp: vorgangstypSchema.optional(),
  abteilungId: z.string().regex(/^\d+$/, "Erwartet wird die Objekt-Id der Kundenabteilung.").optional(),
}).merge(kpiTicketEingrenzungSchema);

const anzahl = z.number().int();
const basis = { spiegel: spiegelStandSchema, jiraBaseUrl: z.string().nullable() };

export const kpiTicketMonatSchema = z.object({
  /** `YYYY-MM` */
  monat: z.string(),
  laufend: z.boolean(),
  erstellt: anzahl,
  geloest: anzahl,
  wiedereroeffnet: anzahl,
  /** Offen am Monatsende, im laufenden Monat heute; künftige Monate null. */
  offen: anzahl.nullable(),
  /** Dasselbe Vorjahr; null, wenn der Vorjahresmonat nach dem Vorjahres-Stichtag liegt oder der Monat noch aussteht. */
  vorjahrErstellt: anzahl.nullable(),
  vorjahrGeloest: anzahl.nullable(),
});
export type KpiTicketMonat = z.infer<typeof kpiTicketMonatSchema>;
/** Summen über die Monate mit Wert: bis heute gegen bis zum selben Tag im Vorjahr. */
export const kpiTicketMonateSummeSchema = z.object({ erstellt: anzahl, geloest: anzahl, vorjahrErstellt: anzahl, vorjahrGeloest: anzahl });
export const kpiTicketMonateSchema = z.object({
  year: z.number().int(),
  monate: z.array(kpiTicketMonatSchema),
  summe: kpiTicketMonateSummeSchema,
  ...basis,
});
export type KpiTicketMonate = z.infer<typeof kpiTicketMonateSchema>;

const kundenAbteilungSchema = z.object({ abteilungId: z.string(), abteilung: z.string(), anzahl, vorjahr: anzahl });
const kundenFirmaSchema = z.object({ firma: z.string(), anzahl, vorjahr: anzahl, abteilungen: z.array(kundenAbteilungSchema) });
export type KpiTicketKundenFirma = z.infer<typeof kundenFirmaSchema>;
export const kpiTicketKundenSchema = z.object({
  zeitraum: zeitraumGrenzenSchema,
  vorjahrZeitraum: zeitraumGrenzenSchema,
  firmen: z.array(kundenFirmaSchema),
  summe: z.object({ anzahl, vorjahr: anzahl }),
  ...basis,
});
export type KpiTicketKunden = z.infer<typeof kpiTicketKundenSchema>;

const slaZeileSchema = z.object({
  abteilungId: z.string(),
  abteilung: z.string(),
  firma: z.string().nullable(),
  /** SLA-Soll „erste Antwort" am Asset, als Auswahltext (`8h`, `Individuell`). */
  soll: z.string().nullable(),
  anzahl,
  eingehalten: anzahl,
  verletzt: anzahl,
  /** Eingehalten in Prozent, null ohne Grundmenge. */
  quote: z.number().nullable(),
  /** Ø Reaktionszeit in Geschäftszeit (ms), nie Kalenderzeit. */
  mittelMs: z.number().nullable(),
  /** Unter `SLA_MINDESTMENGE` — Prozent ohne Grundmenge ist eine Scheinaussage. */
  wenige: z.boolean(),
});
export type KpiTicketSlaZeile = z.infer<typeof slaZeileSchema>;
export const kpiTicketSlaSchema = z.object({
  zeitraum: zeitraumGrenzenSchema,
  abteilungen: z.array(slaZeileSchema),
  summe: z.object({ anzahl, eingehalten: anzahl, verletzt: anzahl, quote: z.number().nullable() }),
  ...basis,
});
export type KpiTicketSla = z.infer<typeof kpiTicketSlaSchema>;

export const kpiTicketWiedereroeffnungSchema = z.object({
  zeitraum: zeitraumGrenzenSchema,
  geloest: anzahl,
  wiedereroeffnet: anzahl,
  /** Wiedereröffnet ÷ gelöst in Prozent; kann über 100 liegen, wenn im Zeitraum mehr Altes wieder aufgeht. */
  quote: z.number().nullable(),
  ...basis,
});
export type KpiTicketWiedereroeffnung = z.infer<typeof kpiTicketWiedereroeffnungSchema>;

const leadSchema = z.object({ employeeId: z.string(), offen: anzahl, erstellt: anzahl, geloest: anzahl, netto: anzahl });
export type KpiTicketLead = z.infer<typeof leadSchema>;
export const kpiTicketLeadsSchema = z.object({ zeitraum: zeitraumGrenzenSchema, leads: z.array(leadSchema), ...basis });
export type KpiTicketLeads = z.infer<typeof kpiTicketLeadsSchema>;

export const kpiTicketListeSchema = z.object({
  tickets: z.array(ticketZeileSchema),
  gekappt: z.boolean(),
  jiraBaseUrl: z.string().nullable(),
});
export type KpiTicketListe = z.infer<typeof kpiTicketListeSchema>;

/** Abfrage der Liegezeit (SR-01); Vorgabe-Zeitraum letzte 30 Tage („Momentaufnahme", Spec). */
export const kpiTicketLiegezeitAbfrageSchema = kpiTicketLeadsAbfrageSchema;

const liegezeitTicketSchema = z.object({
  key: z.string(),
  summary: z.string(),
  customerLabel: z.string().nullable(),
  status: z.string().nullable(),
  /** Aktive Arbeitstage im Abschnitt dieser Person. */
  liegezeitTage: z.number(),
  /** Beginn des Abschnitts (ISO, UTC). */
  seit: z.string(),
  /** Nur abgeschlossene: Zeitpunkt und Art des Abschlusses. */
  abschluss: z.string().nullable(),
  art: z.enum(["geloest", "abgegeben"]).nullable(),
});
const liegezeitPersonSchema = z.object({
  /** Atlassian-Konto-Kennung (Bezug), Name nur zur Anzeige. */
  konto: z.string(),
  name: z.string(),
  /** Abschnitte mit Abschluss im Zeitraum, Ø ihrer Liegezeit. */
  abgeschlossen: z.number().int(),
  mittelTage: z.number().nullable(),
  /** Tickets, die heute bei der Person liegen und nicht erledigt sind. */
  offen: z.number().int(),
  laengsteTage: z.number().nullable(),
  offeneTickets: z.array(liegezeitTicketSchema),
  abgeschlosseneTickets: z.array(liegezeitTicketSchema),
});
export type KpiTicketLiegezeitPerson = z.infer<typeof liegezeitPersonSchema>;
export const kpiTicketLiegezeitSchema = z.object({
  zeitraum: zeitraumGrenzenSchema,
  personen: z.array(liegezeitPersonSchema),
  spiegel: spiegelStandSchema,
  jiraBaseUrl: z.string().nullable(),
});
export type KpiTicketLiegezeit = z.infer<typeof kpiTicketLiegezeitSchema>;
